import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';

const HeaderNotificationListener = () => {
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        let timer;
        const handleToast = (event) => {
            const { type, message, duration } = event.detail;
            setNotification({ type, message, duration, id: Date.now() });

            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                setNotification(null);
            }, duration);
        };

        window.addEventListener('CUSTOM_HEADER_TOAST', handleToast);
        return () => {
            window.removeEventListener('CUSTOM_HEADER_TOAST', handleToast);
            if (timer) clearTimeout(timer);
        };
    }, []);

    const handleClose = () => {
        setNotification(null);
    };

    if (!notification) return null;

    const getColors = (type) => {
        switch (type) {
            case 'success':
                return { bg: '#e6f4ea', border: '#34a853', text: '#137333', icon: <CheckCircle2 size={20} color="#137333" /> };
            case 'error':
                return { bg: '#fce8e6', border: '#ea4335', text: '#b31412', icon: <XCircle size={20} color="#b31412" /> };
            case 'warning':
                return { bg: '#fef7e0', border: '#fbbc04', text: '#b06000', icon: <AlertCircle size={20} color="#b06000" /> };
            case 'info':
                return { bg: '#e8f0fe', border: '#4285f4', text: '#174ea6', icon: <Info size={20} color="#174ea6" /> };
            default:
                return { bg: '#e6f4ea', border: '#34a853', text: '#137333', icon: <CheckCircle2 size={20} color="#137333" /> };
        }
    };

    const colors = getColors(notification.type);

    return (
        <AnimatePresence>
            <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="d-flex align-items-center justify-content-between w-100 h-100 rounded-3 px-3 shadow-sm"
                style={{
                    backgroundColor: colors.bg,
                    border: `1px solid ${colors.border}`,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center justify-content-center">
                        {colors.icon}
                    </div>
                    <span className="fw-bold fs-9" style={{ color: colors.text }}>
                        {notification.message}
                    </span>
                </div>
                <button 
                    onClick={handleClose}
                    className="btn btn-link p-0 hover-opacity-100 flex-shrink-0 d-flex"
                    style={{ color: colors.text, opacity: 0.7, zIndex: 10 }}
                >
                    <X size={18} />
                </button>
                
                <style>
                    {`
                        @keyframes shrinkWidth {
                            from { width: 100%; }
                            to { width: 0%; }
                        }
                    `}
                </style>
                {/* Countdown Progress Bar */}
                <div 
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: '4px',
                        backgroundColor: colors.border,
                        opacity: 0.8,
                        animation: `shrinkWidth ${notification.duration}ms linear forwards`
                    }}
                />
            </motion.div>
        </AnimatePresence>
    );
};

export default HeaderNotificationListener;
